import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup, Validators, FormBuilder, FormArray } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { NotificationLevel, NotificationsService } from 'src/app/services/api/notifications/notifications.service';
import { MasterDataService, Spec, FormItems } from '../../../../services/api/masterdata/masterdata.service';
import { MasterDataComponent } from '../master-data.component';
import { SpecObject } from 'src/app/models/master-data/spec-object';
import { FormItemObject } from 'src/app/models/master-data/form-item-object';

@Component({
  selector: 'app-update-master-data',
  templateUrl: './update-master-data.component.html',
  styleUrls: ['./update-master-data.component.scss']
})
export class UpdateMasterDataComponent implements OnInit {

  spec:Spec;
  formItems: FormItems[];
  form: FormGroup;
  formItemsDeleted: FormItems[] = [];

  tooltip = {
    submit: '',
    reset: '',
  };

  constructor(private translate: TranslateService,
    private formBuilder: FormBuilder,
    private notificationsService: NotificationsService,
    private masterDataService:MasterDataService,
    public dialogRef: MatDialogRef<MasterDataComponent>,
     @Inject(MAT_DIALOG_DATA) private data: {
    spec: Spec;
  },) { }
  
  get typeForm(){
    return this.form.get('type');
  }
  get descForm(){
    return this.form.get('desc');
  }
  get formItemsForm(){
    return this.form.get('formItemsForm') as FormArray;
  }

  ngOnInit() {
    this.spec = this.data.spec
    this.formItems = this.spec.formItems    
    this.initForm();
    this.initTooltips();
  }

  initForm(){
    this.form = this.formBuilder.group({
      type: this.formBuilder.control({value:this.spec.type,disabled: true},Validators.required),
      desc: this.formBuilder.control({value:this.spec.value,disabled:false},Validators.required),
      formItemsForm: this.formBuilder.array([])
    });
    for (let condition of this.formItems){
      this.addFormItemWithValues(condition.hjid,condition.value,condition.description);
    }
  }


  private initTooltips() {
    this.translate.get('DUMMY.key').subscribe((translated: string) => {
      this.tooltip.submit = this.translate.instant('TOOLTIPS.submitForm');
      this.tooltip.reset = this.translate.instant('TOOLTIPS.resetForm');
    });
  }

  clearForm() {
    this.form.reset();
    this.initForm();
  }

  public submitForm() {
    if (this.validateForm()) {
      const specUpdated: Spec = new SpecObject();
      specUpdated.hjid = this.spec.hjid;
      specUpdated.type = this.form.get("type").value;
      specUpdated.value = this.form.get("desc").value;
      specUpdated.description = this.spec.description;
  
      let formItemUpdated: FormItems[] = [];
      let formItemsToUpdate: FormItems[] = [];
      let formItemsToAdd: FormItems[] = [];
      let formItemsDeleted: FormItems[] = [];
  
      for (let item of this.formItemsForm.controls) {
        const itemUpdated: FormItems = new FormItemObject();
        itemUpdated.hjid = item.get("hjid").value;
        itemUpdated.value = item.get("value").value;
        itemUpdated.description = item.get("desc").value;
        itemUpdated.specId = this.spec; 
  
        formItemUpdated.push(itemUpdated);
  
        if (itemUpdated.hjid) {
          if (this.isFormItemChanged(itemUpdated, item as FormGroup)) {
            formItemsToUpdate.push(itemUpdated);
          }
        } else {
          formItemsToAdd.push(itemUpdated);
        }
      }
  
      formItemsDeleted.push(...this.formItemsDeleted);
  
      specUpdated.formItems = formItemUpdated;
  
      this.masterDataService.updateSpec(specUpdated).subscribe(() => {  
        let promises: Promise<any>[] = [];
        for (let formItem of formItemsToUpdate) {
          promises.push(this.masterDataService.updateFormItem(formItem).toPromise());
        }
  
        for (let formItem of formItemsToAdd) {
          promises.push(this.masterDataService.addFormItem(formItem).toPromise());
        }
  
        for (let formItemDeleted of formItemsDeleted) {
          promises.push(this.masterDataService.deleteFormItem(formItemDeleted.hjid).toPromise());
        }
  
        Promise.all(promises).then(() => {
          this.notificationsService.showNotification('Todos los elementos del formulario se han actualizado correctamente', NotificationLevel.SUCCESS);
          this.dialogRef.close();
        }).catch(error => {
          this.notificationsService.showNotification(`Ha habido un problema al actualizar los elementos del formulario:\n${error.error.errorDesc.stackTrace}`, NotificationLevel.ERROR);
        });
      }, error => {
        this.notificationsService.showNotification(`Ha habido un problema al actualizar el dato maestro:\n${error.error.errorDesc.stackTrace}`, NotificationLevel.ERROR);
      });
    } 
  }
  
  isFormItemChanged(item: FormItems, formGroup: FormGroup): boolean {
    const originalItem = this.formItems.find(original => original.hjid === item.hjid);
  
    if (!originalItem) {
      return false; 
    }
  
    const valueChanged = originalItem.value !== formGroup.get('value').value;
    const descChanged = originalItem.description !== formGroup.get('desc').value;
  
    return valueChanged || descChanged;
  }
  
  validateForm(): boolean {
    if (!this.form.get("desc").value) {
      this.notificationsService.showNotification('Por favor, complete la descripción del tipo de dato maestro.', NotificationLevel.WARNING);
      return false; 
    }

    for(let item of this.formItemsForm.controls){
      if(!item.get("value").value ||!item.get("desc").value){
        this.notificationsService.showNotification(`Por favor, complete todos los campos.`, NotificationLevel.WARNING);
        return false;
      }
    }
    return true;   
  }
   

  addFormItemWithValues(hjid: string, value: string, description: string){
    const formItemFormGroup  = this.formBuilder.group({
      hjid: hjid,
      value: this.formBuilder.control({value:value,disabled: false},Validators.required),
      desc: this.formBuilder.control({value:description,disabled: false},Validators.required)
    });
    this.formItemsForm.push(formItemFormGroup);
  }

  addFormItem(){
    const formItemFormGroup  = this.formBuilder.group({
      hjid: '',
      value: '',
      desc: ''
    });
    this.formItemsForm.push(formItemFormGroup);
  }

  removeFormItem(indice: number) {
    const formItem = this.formItemsForm.at(indice).value;
    if (formItem.hjid) {
      this.formItemsDeleted.push(formItem);
    }
    this.formItemsForm.removeAt(indice);  }
}
